import { useState } from 'react';

import { analytics } from '~entities/amplitude-analytics';
import {
  isChallengeConfirmationError,
  useConfirmMutation,
  useResendConfirmationMutation,
} from '~entities/auth';
import { orderAtom } from '~entities/order';

import { isAuthError } from '~shared/errors';
import { useOnMountEffect } from '~shared/hooks';

import { useAtomValue, useSetAtom } from 'jotai';

import { OtpConfirmation } from './otp-confirmation';
import type { ConfirmationErrorType } from './otp-confirmation';

import { resetLastOtpRequestsAtom } from '../model';

interface Props {
  phoneNumber: string;
  onSuccess(): void;
  onAuthError?(): void;
}

export const AuthByOtp: React.FC<Props> = ({
  phoneNumber,
  onSuccess,
  onAuthError,
}) => {
  const [confirmationError, setConfirmationError] =
    useState<ConfirmationErrorType>();

  const resetLastOtpRequests = useSetAtom(resetLastOtpRequestsAtom);

  const order = useAtomValue(orderAtom);

  useOnMountEffect(() => {
    if (order) {
      analytics.track.ponOtpConfirmationView({
        order_id: order.id,
        phone_number: phoneNumber,
      });
    }
  });

  const confirmMutation = useConfirmMutation({
    onError: (error) => {
      if (isChallengeConfirmationError(error)) {
        setConfirmationError('confirmationError');
        if (order) {
          analytics.track.ponOtpConfirmationWrongCode({
            order_id: order.id,
            phone_number: phoneNumber,
          });
        }
        return;
      }
      if (isAuthError(error as Error)) {
        setConfirmationError(undefined);
        onAuthError?.();
        return;
      }
      setConfirmationError('unexpectedError');
    },
    onSuccess: () => {
      resetLastOtpRequests(phoneNumber);
      onSuccess();
      if (order) {
        analytics.track.ponOtpConfirmationSuccess({
          order_id: order.id,
          phone_number: phoneNumber,
        });
      }
    },
  });
  const { mutateAsync: resendConfirmationAsync } =
    useResendConfirmationMutation();

  return (
    <OtpConfirmation
      phoneNumber={phoneNumber}
      confirmationError={confirmationError}
      isConfirmationInProgress={confirmMutation.isPending}
      onCodeSubmit={(answer) => {
        setConfirmationError(undefined);
        confirmMutation.mutate({ answer });
      }}
      onCodeResend={resendConfirmationAsync}
      onCodeResendLimitError={() => {
        setConfirmationError(undefined);
        onAuthError?.();
      }}
    />
  );
};
